import {
  GetExperimentRequest,
  TagExperimentRequest,
  UntagExperimentRequest,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_service_pb';
import { RegisteredModelVersion } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_pb';
import { Tag } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/tag_pb';
import { ExperimentService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/experiment_service_connect';
import { RegisteredModelVersionService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_version_service_connect';
import { TagService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/tag_service_connect';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { IContextualMenuItem, IStyle, MessageBarType } from '@fluentui/react';
import { ClassNamesFromIStyles, IH2OTheme, useClassNames, useTheme, useToast } from '@h2oai/ui-kit';
import React from 'react';
import { useHistory } from 'react-router-dom';

import SelectableItemsCallout from '../../components/SelectableItemCallout/SelectableItemsCallout';
import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { ENDPOINTS } from './constants';
import { useModelTags } from './ModelTagProvider';
import { GranularMLOpsPermissions } from './userActions';

export type RegisteredModelVersionWithTag = RegisteredModelVersion & {
  tag?: Tag[];
};

// Adjust this number based on how many tags you want to display.
const MAX_VISIBLE_TAGS = 2;
interface ITaggingWidgetStyles {
  tag: IStyle;
  moreTagsPlaceholder: IStyle;
}

const taggingWidgetStyles = (theme: IH2OTheme): Partial<ITaggingWidgetStyles> => ({
  tag: {
    padding: '2px 4px',
    background: theme.palette?.gray300,
    borderRadius: 24,
    marginRight: 2,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    height: 20,
  },
  moreTagsPlaceholder: {
    padding: '2px 4px',
    background: theme.palette?.gray300,
    borderRadius: 24,
    marginRight: 2,
    lineHeight: '16px',
    height: 20,
  },
});

const ModelTaggingWidget = ({
  modelVersionId,
  isInTheModelList = false,
  permissions,
}: {
  modelVersionId: string;
  isInTheModelList?: boolean;
  permissions: GranularMLOpsPermissions;
}) => {
  const theme = useTheme(),
    classNames = useClassNames<ITaggingWidgetStyles, ClassNamesFromIStyles<ITaggingWidgetStyles>>(
      'modelTaggingWidget',
      taggingWidgetStyles(theme)
    ),
    // "isInTheModelList" specifies if the widget is rendered in the model list or in the model detail page.
    // If it is rendered in the model list, it is under the modelTags context provider.
    // Therefore it does not violate "React Hooks must be called in the exact same order in every component render." policy.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    { setAreTagsUpdated } = isInTheModelList ? useModelTags() : { setAreTagsUpdated: () => {} },
    [tags, setTags] = React.useState<Tag[]>(),
    history = useHistory(),
    { addToast } = useToast(),
    { ACTIVE_WORKSPACE_NAME, ACTIVE_WORKSPACE_ID } = useWorkspaces(),
    loadStateRef = React.useRef({
      loadingTags: false,
      loadingModelVersion: false,
      settingVersionTags: false,
    }),
    // TODO: Handle tag loading.
    [, setLoading] = React.useState(true),
    [modelVersion, setModelVersion] = React.useState<RegisteredModelVersionWithTag>(),
    [tagItems, setTagItems] = React.useState<IContextualMenuItem[]>(),
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    storageTransport = createConnectTransport({
      baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
    }),
    evaluateLoading = () => {
      if (
        !loadStateRef.current.loadingTags &&
        !loadStateRef.current.loadingModelVersion &&
        !loadStateRef.current.settingVersionTags
      ) {
        setLoading(false);
      }
    },
    tagClient = createClient(TagService, storageTransport),
    experimentClient = createClient(ExperimentService, storageTransport),
    registerModelVersionClient = createClient(RegisteredModelVersionService, storageTransport),
    tagExperiment = React.useCallback(
      async (experimentId: string, tagId: string) => {
        try {
          await experimentClient.tagExperiment(
            new TagExperimentRequest({
              experimentId,
              tagId,
            })
          );
          getModelVersion(modelVersionId);
          if (isInTheModelList) setAreTagsUpdated(false);
        } catch (err) {
          const message = `Failed to tag experiment: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        }
      },
      [modelVersionId, isInTheModelList]
    ),
    untagExperiment = React.useCallback(
      async (experimentId: string, tagId: string) => {
        try {
          await experimentClient.untagExperiment(
            new UntagExperimentRequest({
              experimentId,
              tagId,
            })
          );
          getModelVersion(modelVersionId);
          if (isInTheModelList) setAreTagsUpdated(false);
        } catch (err) {
          const message = `Failed to untag experiment: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        }
      },
      [modelVersionId, isInTheModelList]
    ),
    fetchTags = React.useCallback(async () => {
      loadStateRef.current.loadingTags = true;
      setLoading(true);
      try {
        const response = await tagClient.listTags({
          // TODO: Use ACTIVE_WORKSPACE_NAME directly when storage is migrated.
          projectId: ACTIVE_WORKSPACE_ID,
        });
        const tags = response?.tag;
        if (!tags) console.error('No tags found in the response.');
        setTags(tags);
      } catch (err) {
        const message = `Failed to fetch tags: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        setTags(undefined);
      } finally {
        loadStateRef.current.loadingTags = false;
        evaluateLoading();
      }
    }, [ACTIVE_WORKSPACE_ID]),
    getExperiment = React.useCallback(
      async (experimentId: string) => {
        try {
          const response = await experimentClient.getExperiment(
            new GetExperimentRequest({
              id: experimentId,
            })
          );
          const experiment = response?.experiment;
          if (!experiment) console.error('No experiment found in the response.');
          return experiment;
        } catch (err) {
          const message = `Failed to fetch experiment metadata: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        }
      },
      [addToast]
    ),
    getModelVersion = React.useCallback(
      async (modelVersionId: string) => {
        if (!modelVersionId) return;
        loadStateRef.current.loadingModelVersion = true;
        setLoading(true);
        try {
          const response = await registerModelVersionClient.getModelVersion({
            modelVersionId,
          });
          const modelVersion = response?.registeredModelVersion;
          if (!modelVersion) {
            console.error('No model version found in the response.');
            addToast({
              messageBarType: MessageBarType.error,
              message: 'No model version found in the response.',
            });
            return;
          }
          const versionExperiment = await getExperiment(modelVersion?.experimentId);
          const tag = versionExperiment?.tag;
          // TODO: Avoid Object.assign.
          setModelVersion(Object.assign(new RegisteredModelVersion(modelVersion), { tag }));
        } catch (err) {
          const message = `Failed to fetch model version: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          setModelVersion(undefined);
        } finally {
          loadStateRef.current.loadingModelVersion = false;
          evaluateLoading();
        }
      },
      [modelVersionId]
    ),
    onManageTags = () => {
      history.push(`/mlops/${ACTIVE_WORKSPACE_NAME}/models/manage-tags`);
    };

  React.useEffect(() => {
    if (ACTIVE_WORKSPACE_NAME) fetchTags();
  }, [fetchTags, ACTIVE_WORKSPACE_NAME]);

  React.useEffect(() => {
    if (!modelVersion || !tags) return;
    setLoading(true);
    loadStateRef.current.settingVersionTags = true;

    const tagItems: IContextualMenuItem[] =
      tags?.map((tag) => {
        const isChecked = modelVersion.tag?.some((modelTag) => modelTag.id === tag.id);
        return {
          key: tag.id,
          text: tag.displayName,
          onClick: permissions?.isAbleToChangeModelTags
            ? () =>
                !isChecked
                  ? void tagExperiment(modelVersion.experimentId, tag.id)
                  : void untagExperiment(modelVersion.experimentId, tag.id)
            : undefined,
          canCheck: permissions?.isAbleToChangeModelTags,
          isChecked,
        };
      }) || [];

    setTagItems(tagItems);

    loadStateRef.current.settingVersionTags = false;
    evaluateLoading();
  }, [modelVersion, tags, permissions?.isAbleToChangeModelTags]);

  React.useEffect(() => {
    getModelVersion(modelVersionId);
  }, [modelVersionId]);

  React.useEffect(() => {
    return () => {
      setTags(undefined);
      setModelVersion(undefined);
      setTagItems(undefined);
    };
  }, []);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center  ' }}>
        <h4 style={{ marginRight: 4, marginTop: 0, marginBottom: 0, paddingBottom: 0 }}>Tags</h4>
        <div style={{ paddingBottom: 0 }}>
          <SelectableItemsCallout
            buttonIconName="Settings"
            menuItems={tagItems || []}
            searchBoxPlaceholderText="Filter tags"
            bottomLinkText={permissions?.isAbleToChangeModelTags ? 'Manage tags' : undefined}
            emptyItemsText="No tags to select from."
            onBottomLinkClick={permissions?.isAbleToChangeModelTags ? onManageTags : undefined}
            calloutNoItemsText="No tags found"
            calloutNoItemsIconName="SearchIssue"
            iconSize={12}
            iconOnly
            isReadOnly={!permissions?.isAbleToChangeModelTags}
            openContextMenuIfNoItems
          />
        </div>
      </div>
      <div style={{ minHeight: 24, display: 'flex', flexWrap: 'nowrap', overflow: 'hidden' }}>
        {tagItems
          ?.filter((item) => item.isChecked)
          ?.map((tag, index, array) => {
            if (index < MAX_VISIBLE_TAGS) {
              return (
                <span title={tag.text} className={classNames.tag} key={tag.key}>
                  {tag.text}
                </span>
              );
            } else if (index === MAX_VISIBLE_TAGS) {
              return (
                <span
                  title={`+${array.length - MAX_VISIBLE_TAGS} more ${
                    array.length - MAX_VISIBLE_TAGS === 1 ? 'tag' : 'tags'
                  }`}
                  className={classNames.moreTagsPlaceholder}
                  key="more-tags"
                >
                  +{array.length - MAX_VISIBLE_TAGS}
                </span>
              );
            }
            return null;
          })}
      </div>
    </div>
  );
};
export default ModelTaggingWidget;
